import { useParams } from 'react-router-dom';
import { Pill, StatusLabel } from '../components/DataTable';
import { PageHeader } from '../components/PageHeader';
import { StatTile } from '../components/StatTile';
import { formatCents } from '../lib/format';
import { useEvents, useRevenueByTool, useTool } from '../lib/queries';

export function ToolDetail() {
  const { slug } = useParams<{ slug: string }>();
  const tool = useTool(slug ?? '');
  const events = useEvents(tool.data?.id);
  const revenue = useRevenueByTool(tool.data?.id);

  if (tool.isLoading) {
    return <div className="text-[13px] text-foreground/55">Loading…</div>;
  }
  if (!tool.data) {
    return <div className="text-[13px] text-foreground/55">Not found.</div>;
  }

  const t = tool.data;
  const sources = revenue.data
    ? Object.entries(revenue.data.by_source).filter(([, v]) => v > 0)
    : [];

  return (
    <div>
      <PageHeader
        breadcrumb={`Tool Factory · Tools · ${t.slug}`}
        title={t.name}
        subtitle={
          <span className="inline-flex items-center gap-3">
            <span className="font-mono text-[12px] text-foreground/65">{t.slug}</span>
            <Pill>{t.kind}</Pill>
            <StatusLabel status={t.status} />
            <span className="text-[12px] text-foreground/55">{t.audience}</span>
          </span>
        }
      />

      <div className="mb-8 grid grid-cols-1 gap-[14px] md:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label="Total revenue · all-time"
          accent="gold"
          value={formatCents(revenue.data?.total_cents ?? 0)}
          delta={sources.length === 0 ? 'No revenue yet' : `${sources.length} sources`}
        />
        {sources.slice(0, 3).map(([source, cents]) => (
          <StatTile key={source} label={source} value={formatCents(cents)} delta="all-time" />
        ))}
        {sources.length === 0 ? (
          <>
            <StatTile label="lead_sale" value={formatCents(0)} delta="—" />
            <StatTile label="subscription" value={formatCents(0)} delta="—" />
            <StatTile label="affiliate" value={formatCents(0)} delta="—" />
          </>
        ) : null}
      </div>

      <section className="mb-8">
        <h2 className="font-heading text-[15px] font-semibold mb-3 tracking-tight">
          Config <span className="text-foreground/40 font-mono text-[12px]">(jsonb)</span>
        </h2>
        <pre className="rounded-lg border border-border bg-card p-4 font-mono text-[12px] text-foreground/85 overflow-auto">
          {JSON.stringify(t.config, null, 2)}
        </pre>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-[15px] font-semibold mb-3 tracking-tight">
          Monetization config{' '}
          <span className="text-foreground/40 font-mono text-[12px]">(jsonb)</span>
        </h2>
        <pre className="rounded-lg border border-border bg-card p-4 font-mono text-[12px] text-foreground/85 overflow-auto">
          {JSON.stringify(t.monetization_config, null, 2)}
        </pre>
      </section>

      <section className="mb-8">
        <h2 className="font-heading text-[15px] font-semibold mb-3 tracking-tight">
          Recent events
        </h2>
        {events.isLoading ? (
          <p className="text-[13px] text-foreground/55">Loading events…</p>
        ) : (events.data ?? []).length === 0 ? (
          <div className="rounded-lg border border-border bg-card px-5 py-8 text-center text-[13px] text-foreground/50">
            No events for this tool yet.
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <ul className="divide-y divide-border/50">
              {events.data?.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-4 px-5 py-3 hover:bg-secondary/35 transition-colors"
                >
                  <span className="font-mono text-[11px] text-foreground/55 w-44 flex-shrink-0">
                    {new Date(e.occurred_at).toLocaleString()}
                  </span>
                  <Pill>{e.event_type}</Pill>
                  <span className="font-mono text-[11px] text-foreground/55">
                    session={e.session_id.slice(0, 8)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}
